/**
 * The products the acquisition workspace can register. The workspace is one
 * page with one tab per product, so the identifier is at once the tab value,
 * the URL vocabulary and the key every product keeps its own state under.
 */
export const ACQUISITION_PRODUCT_IDS = [
  'mutui',
  'prestiti',
  'cessioni-del-quinto',
] as const;

export type AcquisitionProductId = (typeof ACQUISITION_PRODUCT_IDS)[number];

/** Italian, because the selection is part of the operator-facing URL. */
export const ACQUISITION_PRODUCT_QUERY_PARAM = 'prodotto';

/**
 * Mutui is canonical: it is the product the page already acquired before the
 * workspace existed, so an absent parameter has to keep meaning Mutui and
 * selecting Mutui has to drop the parameter again.
 */
export const DEFAULT_ACQUISITION_PRODUCT: AcquisitionProductId = 'mutui';

export const ACQUISITION_PRODUCT_LABEL: Record<AcquisitionProductId, string> = {
  mutui: 'Mutui',
  prestiti: 'Prestiti',
  'cessioni-del-quinto': 'Cessioni del quinto',
};

/**
 * Anything the URL cannot name is not an error the operator has to resolve:
 * a stale or hand-edited link simply lands on the canonical product.
 */
export const normalizeAcquisitionProduct = (
  raw: string | null | undefined
): AcquisitionProductId =>
  ACQUISITION_PRODUCT_IDS.includes(raw as AcquisitionProductId)
    ? (raw as AcquisitionProductId)
    : DEFAULT_ACQUISITION_PRODUCT;

/** `null` where the product is canonical and the parameter must be absent. */
export const acquisitionProductQueryValue = (
  product: AcquisitionProductId
): string | null =>
  product === DEFAULT_ACQUISITION_PRODUCT ? null : product;

export const acquisitionTabId = (product: AcquisitionProductId) =>
  `acquisition-tab-${product}`;

export const acquisitionPanelId = (product: AcquisitionProductId) =>
  `acquisition-panel-${product}`;
